import React from 'react';
import { observer } from 'mobx-react';

import { DangerAlert, TableListingPage, IBasicTableColumn } from 'ui-lib';

import { IDisplayKey } from '../../../store/key/key-store';
import { confirmDelete } from '../utils';
import { UploadKeysComponent } from './upload-keys';

import manageKeyCss from './keys.css';
import { useKeyStore } from 'store';
import { getErrorMessage } from 'common-utils';
import { PSP } from 'dto/key-management/key-dto';
import { KEY_PERMISSIONS, usePermissionContext } from 'contexts/permissions/permission-provider';
import { TableUtils } from '../components/table-utils';

export enum KEY_INVENTORY_PAGE {
    LIST,
    ADD,
}

function deleteKey(id: number, action: any) {
    confirmDelete(id, 'Do you want to delete this key?', action);
}

export const KeyInventoryPage = observer(() => {
    const permissionContext = usePermissionContext();
    const keyStore = useKeyStore();
    const [currentPage, setCurrentPage] = React.useState<KEY_INVENTORY_PAGE>(KEY_INVENTORY_PAGE.LIST);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const onDelete = async (id: number) => keyStore.deleteKey(id).catch((err) => setErrorMessage(getErrorMessage(err)));

    const onAdd = () => setCurrentPage(KEY_INVENTORY_PAGE.ADD);

    const columns: IBasicTableColumn[] = [
        TableUtils.createBasicEnumFilterColumn({
            key: 'psp',
            title: 'PSP',
            filters: Object.values(PSP).map((item) => ({ value: item, text: item })),
        }),
        TableUtils.createBasicFilterColumn({ key: 'keyId', title: 'Key id', searchPlaceholder: 'Search key id', columnExtraOptions: { sorter: (a, b) => a.keyId - b.keyId } }),
        TableUtils.createBasicFilterColumn({ key: 'keyName', title: 'Key name', searchPlaceholder: 'Search key name' }),
        {
            code: 'partnerOrClient',
            title: 'Partner/Client',
            dataIndex: 'partenOrClient',
            textSearchPlaceholder: 'Search partner/client',
            onFilter: (accountName: string, customerKey: IDisplayKey) =>
                customerKey.partenOrClient.toLowerCase().includes(accountName.toLowerCase()),
        },
        TableUtils.createBasicFilterTimeColumn({ key: 'dateUploaded', title: 'Date loaded', defaultSortOrder: 'descend' }),
        ...(permissionContext.hasPermission(KEY_PERMISSIONS.KEY_INVENTORY_MODIFY)
            ? [
                  {
                      code: 'action',
                      title: 'Action',
                      dataIndex: 'action',
                      actions: [
                          {
                              key: 'id',
                              label: 'Delete',
                              onClick: (id: number) => deleteKey(id, onDelete),
                          },
                      ],
                  },
              ]
            : []),
    ];

    const toListPage = () => setCurrentPage(KEY_INVENTORY_PAGE.LIST);

    return currentPage === KEY_INVENTORY_PAGE.LIST ? (
        <div className={manageKeyCss.key_inventory}>
            {errorMessage ? <DangerAlert message={errorMessage} className={manageKeyCss.alert} /> : null}
            <TableListingPage
                tableProps={{
                    title: '',
                    loadingState: keyStore.dataLoading,
                    table: {
                        columns,
                        dataSource: keyStore.displayKeys,
                        rowKey: 'id',
                    },
                    toolbar: {
                        onAdd: permissionContext.hasPermission(KEY_PERMISSIONS.KEY_INVENTORY_MODIFY) ? onAdd : undefined,
                    },
                }}
            />
        </div>
    ) : (
        <UploadKeysComponent onCancel={toListPage} onOk={toListPage} />
    );
});
